import React, { useState, useEffect, useRef } from 'react';
import { useTimer } from '../contexts/TimerContext';
import { useSettings } from '../contexts/SettingsContext';
import { api } from '../config/api';
import './SnacksSelection.css';

const SnacksSelection = ({ movie, showtime, theater, selectedSeats, onSnacksUpdate, onProceedToPayment, initialSnacks = [] }) => {
  const { formattedTime, isExpired, timeRemaining } = useTimer();
  const [selectedCategory, setSelectedCategory] = useState('combos');
  const [cart, setCart] = useState(initialSnacks || []);
  const [vegFilter, setVegFilter] = useState(false);
  const [nonVegFilter, setNonVegFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingSnacks, setLoadingSnacks] = useState(false);
  const [apiSnacksData, setApiSnacksData] = useState(null);
  // const [fbGstPercent, setFbGstPercent] = useState(5);
  // const [convenienceFeeSettings, setConvenienceFeeSettings] = useState({ enabled: false, type: 'fixed', value: 0 });
  // const [bookingFeeTiers, setBookingFeeTiers] = useState(null);
  
  const { settings } = useSettings();

  
  const [cinemaData, setCinemaData] = useState(null);
  const wrapRef = useRef(null);

  const pillsRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const fbGstPercent = settings?.fnbGst || 0;

  const convenienceFeeSettings = settings?.convenienceFee || {
    enabled: settings?.enableConvenienceFee || false,
    type: settings?.convenienceFeeType || 'fixed',
    value: settings?.convenienceFeeValue || 0
  };

  const bookingFeeTiers = settings?.bookingFeeTiers || null;
  
  const handlePillsScroll = () => {
    const el = pillsRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };
  
  useEffect(() => {
    const el = pillsRef.current;
    if (!el) return;
    // check on mount whether right arrow is needed at all
    setShowRightArrow(el.scrollWidth > el.clientWidth);
    el.addEventListener('scroll', handlePillsScroll);
    return () => el.removeEventListener('scroll', handlePillsScroll);
  }, [apiSnacksData]);
  
  const scrollPills = (dir) => {
    if (pillsRef.current) pillsRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  useEffect(() => {

    if (window.Tawk_API) {
      window.Tawk_API.hideWidget();
    }
  
    return () => {
      if (window.Tawk_API) {
        window.Tawk_API.showWidget();
      }
    };
  
  }, []);

  // Measure navbar + stepper and set top so the fixed overlay sits below both
  useEffect(() => {
    const setTop = () => {
      // Try to find the stepper/progress bar first (sits below navbar)
      const stepper = document.querySelector('.stepper') 
        || document.querySelector('.progress-bar-container')
        || document.querySelector('.booking-steps')
        || document.querySelector('[class*="stepper"]')
        || document.querySelector('[class*="step-bar"]')
        || document.querySelector('[class*="progress"]');

      const nav = document.querySelector('nav') 
        || document.querySelector('header') 
        || document.querySelector('.navbar');

      if (wrapRef.current) {
        // Use the bottom of whichever element is lower on the page
        const navBottom = nav ? nav.getBoundingClientRect().bottom : 0;
        const stepperBottom = stepper ? stepper.getBoundingClientRect().bottom : 0;
        const top = Math.max(navBottom, stepperBottom);
        wrapRef.current.style.top = top + 'px';
      }
    };
    setTop();
    window.addEventListener('resize', setTop);
    return () => window.removeEventListener('resize', setTop);
  }, []);

  // useEffect(() => {
  //   const fetchSettings = async () => {
  //     try {
  //       const settings = await api.getSettings();
  //       if (settings?.convenienceFee) setConvenienceFeeSettings(settings.convenienceFee);
  //       if (settings?.bookingFeeTiers?.length) setBookingFeeTiers(settings.bookingFeeTiers);
  //       setFbGstPercent(
  //         settings?.fnbGst !== undefined
  //           ? parseFloat(settings.fnbGst)
  //           : 5
  //       );
  //     } catch (e) { console.error(e); }
  //   };
  //   fetchSettings();
  // }, []);

  useEffect(() => {
    const fetchSnacks = async () => {
      const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.vistacinemaid;
      const cinemaId = vistaCinemaId || theater?.id;
      const empty = { combos: [], popcorn: [], beverages: [], juices: [], burgers: [], snacks: [], desserts: [] };
      if (!cinemaId) { setApiSnacksData(empty); return; }
      setLoadingSnacks(true);
      try {
        const snacks = await api.getSnacks(cinemaId, !!vistaCinemaId);
        setApiSnacksData(Array.isArray(snacks) && snacks.length ? transformVistaSnacks(snacks) : empty);
      } catch { setApiSnacksData(empty); }
      finally { setLoadingSnacks(false); }
    };
    fetchSnacks();
  }, [theater?.vistaCinemaId, theater?.id]);

  useEffect(() => {
    const fetchCinema = async () => {
      if (!theater) return;
      const id = theater?.id;
      const isUUID = id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      if (id && isUUID) {
        try { const d = await api.getCinemaById(id); if (d) { setCinemaData(d); return; } } catch {}
      }
      const vid = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.vistacinemaid || (!isUUID ? id : null);
      if (vid) {
        try {
          const all = await api.getCinemas({ limit: 1000 });
          const found = all.find(c => [c.vistaCinemaId, c.vistacinemaid, c.vista_cinema_id].map(String).includes(String(vid)));
          if (found?.id) { const d = await api.getCinemaById(found.id); if (d) { setCinemaData(d); return; } }
        } catch {}
      }
      setCinemaData(null);
    };
    fetchCinema();
  }, [theater]);

  const transformVistaSnacks = (items) => {
    const out = { combos: [], popcorn: [], beverages: [], juices: [], burgers: [], snacks: [], desserts: [] };
    items
    .filter(item => parseFloat(item.price || 0) > 0) 
    .forEach((item, i) => {
      const price = parseFloat(item.price || item.amount || item.cost || 0) / 100;
      const id = item.itemId || item.id || item.concessionId || String(i + 1);
      // const name = item.name || item.itemName || item.description || 'Snack Item';
      let name = item.name || item.itemName || item.description || 'Snack Item';
      name = name.replace(/^BMS-/, '');
      name = name.replace(/\(W\)/g, '').trim();
      const cat = categorizeSnack(item, name);
      const obj = { id, itemId: id, name, price, image: item.image || item.icon || getDefaultIcon(cat),
        isVeg: item.isVeg ?? item.vegetarian ?? true, customizable: item.customizable ?? false };
      (out[cat] || out.snacks).push(obj);
    });
    return out;
  };

  const categorizeSnack = (item, name) => {
    const n = name.toLowerCase(), c = (item.category || item.type || '').toLowerCase();
    if (c.includes('combo') || n.includes('combo')) return 'combos';
    if (c.includes('popcorn') || n.includes('popcorn')) return 'popcorn';
    if (c.includes('burger') || n.includes('burger')) return 'burgers';
    if (c.includes('dessert') || n.includes('brownie') || n.includes('ice cream')) return 'desserts';
    if (c.includes('juice') || n.includes('juice') || ['orange','apple','mango','pineapple','grape','watermelon','pomegranate','guava'].some(k=>n.includes(k))) return 'juices';
    if (c.includes('beverage') || c.includes('drink') || ['cola','pepsi','coke','sprite','fanta','mirinda','soda','7up','mountain dew'].some(k=>n.includes(k))) return 'beverages';
    if (c.includes('snack')) return 'snacks';
    return 'snacks';
  };

  const getDefaultIcon = (cat) => {
    const m = { combos:'🍿🥤', popcorn:'🍿', beverages:'🥤', juices:'🧃', burgers:'🍔', desserts:'🍰' };
    return m[cat] || '🍟';
  };

  useEffect(() => {
    if (initialSnacks?.length) {
      setCart(prev => JSON.stringify(prev) !== JSON.stringify(initialSnacks) ? initialSnacks : prev);
    } else if (!initialSnacks?.length && cart.length) {
      setCart([]);
    }
  }, [initialSnacks]);

  useEffect(() => { if (onSnacksUpdate) onSnacksUpdate(cart); }, [cart]);

  const categories = [
    { id: 'combos', name: 'Combos', icon: '🍿🥤' },
    { id: 'popcorn', name: 'Popcorn', icon: '🍿' },
    { id: 'beverages', name: 'Aerated Beverages', icon: '🥤' },
    { id: 'juices', name: 'Juices', icon: '🧃' },
    { id: 'burgers', name: 'Burgers', icon: '🍔' },
    { id: 'snacks', name: 'Snacks', icon: '🍟' },
    { id: 'desserts', name: 'Desserts', icon: '🍰' },
  ];

  const offers = [
    { id: 1, text: 'UPI Payment - 15% Cashback on F&B', icon: '📱' },
    { id: 2, text: 'Credit Card - 20% Off on Food & Beverages', icon: '💳' },
    { id: 3, text: 'Digital Wallet - 10% Instant Discount', icon: '💰' },
  ];

  const data = apiSnacksData || { combos:[], popcorn:[], beverages:[], juices:[], burgers:[], snacks:[], desserts:[] };
  const pool = searchQuery.trim() ? Object.values(data).flat() : (data[selectedCategory] || []);
  const filtered = pool.filter(s => {
    if (vegFilter && !s.isVeg) return false;
    if (nonVegFilter && s.isVeg) return false;
    if (searchQuery.trim()) return s.name.toLowerCase().includes(searchQuery.toLowerCase());
    return true;
  });

  const addToCart = (s) => {
    const ex = cart.find(i => i.id === s.id);
    setCart(ex ? cart.map(i => i.id === s.id ? {...i, quantity: i.quantity+1} : i) : [...cart, {...s, quantity:1}]);
  };
  const removeFromCart = (id) => setCart(cart.filter(i => i.id !== id));
  const updateQty = (id, qty) => { if (qty <= 0) removeFromCart(id); else setCart(cart.map(i => i.id === id ? {...i, quantity:qty} : i)); };

  const ticketTotal = () => selectedSeats?.reduce((t,s) => t + (s.price||200), 0) || 0;
  // const roundTo2 = (value) => Number((value + 0.00001).toFixed(2));
  const roundTo2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
  const getSnackPrice = (price) => roundTo2(price * (1 + fbGstPercent / 100));
  const snacksTotal = () => {
    return roundTo2(
      cart.reduce((t, i) => t + getSnackPrice(i.price) * i.quantity, 0)
    );    
    // const base = cart.reduce((t, i) => t + i.price * i.quantity, 0);
    // const gst = (base * fbGstPercent) / 100; // commenetd out for gst bug
    // return roundTo2(base );
  }; 
  const convFee = () => {
    if (!selectedSeats?.length) return 0;
    if (bookingFeeTiers?.length) {
      const tot = ticketTotal();
      const tier = bookingFeeTiers.find(t => tot >= (parseFloat(t.min_price)||0) && tot <= (parseFloat(t.max_price)||0));
      return tier ? (parseFloat(tier.booking_fee)||0) * selectedSeats.length : 0;
    }
    if (!convenienceFeeSettings.enabled) return 0;
    const {type, value} = convenienceFeeSettings;
    return type === 'fixed' ? value * selectedSeats.length : (ticketTotal() * value) / 100;
  };
  const grandTotal = () => {
    const total = ticketTotal() + snacksTotal() + convFee();
    return (total);
  };
  const cartCount = cart.reduce((t,i) => t + i.quantity, 0);

  return (
    <div className="sc-wrap" ref={wrapRef}>
      {/* ── MAIN BODY: left + right ── */}
      <div className="sc-body">
        {/* ── LEFT PANEL ── */}
        <div className="sc-left">
          {/* Header */}
          <div className="sc-header">
            <h4>Order Snacks</h4>
            <div className="sc-theater">
              <span>📍</span>
              <span>{cinemaData?.name || theater?.name || "My Cinemas"}</span>
            </div>
          </div>

          {/* Offers */}
          <div className="sc-offers">
            {offers.map((o) => (
              <div key={o.id} className="sc-offer-card">
                <span className="sc-offer-icon">{o.icon}</span>
                <span className="sc-offer-text">{o.text}</span>
              </div>
            ))}
          </div>

          {/* Filter row: Veg | NonVeg | | categories */}
          <div className="sc-filters">
            {showLeftArrow && (
              <button
                className="sc-arrow sc-arrow-left"
                onClick={() => scrollPills(-1)}
              >
                ‹
              </button>
            )}

            <div className="sc-pills-scroll" ref={pillsRef}>
              <label className="sc-toggle">
                <input
                  type="checkbox"
                  checked={vegFilter}
                  onChange={(e) => {
                    setVegFilter(e.target.checked);
                    if (e.target.checked) setNonVegFilter(false);
                  }}
                />
                <span className="sc-track"></span> Veg
              </label>
              <label className="sc-toggle">
                <input
                  type="checkbox"
                  checked={nonVegFilter}
                  onChange={(e) => {
                    setNonVegFilter(e.target.checked);
                    if (e.target.checked) setVegFilter(false);
                  }}
                />
                <span className="sc-track"></span> Non Veg
              </label>
              <div className="sc-divider"></div>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`sc-pill${selectedCategory === cat.id ? " active" : ""}`}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setSearchQuery("");
                  }}
                >
                  {cat.icon} {cat.name}
                </button>
              ))}
            </div>

            {showRightArrow && (
              <button
                className="sc-arrow sc-arrow-right"
                onClick={() => scrollPills(1)}
              >
                ›
              </button>
            )}
          </div>
          {/* Items — ONLY THIS SCROLLS */}
          <div className="sc-items">
            {loadingSnacks ? (
              <div className="sc-empty">Loading snacks...</div>
            ) : !apiSnacksData ? (
              <div className="sc-empty">No snacks available at this cinema</div>
            ) : filtered.length === 0 ? (
              <div className="sc-empty">
                {searchQuery.trim()
                  ? "No snacks found"
                  : "No snacks in this category"}
              </div>
            ) : (
              filtered.map((snack) => {
                const cartItem = cart.find((i) => i.id === snack.id);
                return (
                  <div key={snack.id} className="sc-item">
                    <div className="sc-item-img">
                      {typeof snack.image === "string" &&
                      snack.image.startsWith("http") ? (
                        <img src={snack.image} alt={snack.name} />
                      ) : (
                        snack.image
                      )}
                    </div>
                    <div className="sc-item-info">
                      <h5>{snack.name}</h5>
                      <p className="sc-item-price">
                        ₹{getSnackPrice(snack.price)}
                        {/* ₹{roundTo2(snack.price * (1 + fbGstPercent / 100))} */}
                        {/* <p className="sc-item-price">₹{roundTo2(snack.price)}</p> */}
                      </p>
                      {snack.customizable && <small>Customizable</small>}
                    </div>
                    <div>
                      {cartItem ? (
                        <div className="sc-qty">
                          <button
                            className="sc-qty-btn"
                            onClick={() =>
                              updateQty(snack.id, cartItem.quantity - 1)
                            }
                          >
                            −
                          </button>
                          <span className="sc-qty-num">
                            {cartItem.quantity}
                          </span>
                          <button
                            className="sc-qty-btn"
                            onClick={() =>
                              updateQty(snack.id, cartItem.quantity + 1)
                            }
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          className="sc-add-btn"
                          onClick={() => addToCart(snack)}
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Bottom bar — always visible, never scrolls */}
          <div className="sc-bottom">
            <div className="sc-cart-info">
              <span className="sc-cart-icon">🛒</span>
              <div className="sc-cart-label">
                <span className="sc-cart-text">
                  {cartCount === 0
                    ? "No items added"
                    : `${cartCount} item${cartCount > 1 ? "s" : ""} added`}
                </span>
                <span className="sc-cart-amount">
                  ₹{snacksTotal().toFixed(2)}
                </span>
              </div>
            </div>
            <div className="sc-search">
              <input
                type="text"
                placeholder={`Search '${categories.find((c) => c.id === selectedCategory)?.name || "Popcorn"}'`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="sc-spacer"></div>
            <button className="sc-continue" onClick={onProceedToPayment}>
              Continue
            </button>
          </div>
        </div>
        {/* end sc-left */}

        {/* ── RIGHT SIDEBAR — fixed height, no scroll ── */}
        <div className="sc-right">
          <div className="sc-summary">
            <h4>Booking Summary</h4>

            {/* Timer */}
            <div className={`sc-timer${isExpired ? " expired" : ""}`}>
              <span className="sc-timer-icon">⏱</span>
              <div className="sc-timer-body">
                <span className="sc-timer-text">
                  {isExpired
                    ? "Session Expired"
                    : `${formattedTime} Mins Remaining`}
                </span>
                <div className="sc-timer-bar-bg">
                  <div
                    className="sc-timer-bar"
                    style={{
                      width: isExpired
                        ? "0%"
                        : `${(timeRemaining / (15 * 60)) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Movie */}
            <div className="sc-movie">
              <img
                src={movie?.poster || "/assets/img/film-banner-booking.png"}
                alt={movie?.title}
              />
              <div className="sc-movie-info">
                <h5>{movie?.title.replace(/\(.*?\)/g, "").trim()}</h5>
                <div className="sc-tags">
                  {movie?.genre &&
                    String(movie.genre)
                      .replace(/[{}"]/g, "")
                      .split(",")
                      .map((g) => g.trim())
                      .filter(Boolean)
                      .map((g, i) => (
                        <span key={i} className="sc-tag">
                          {g}
                        </span>
                      ))}
                  {movie?.language && (
                    <span className="sc-tag">{movie.language}</span>
                  )}
                  {(movie?.ageRating || movie?.rating) && (
                    <span className="sc-tag">
                      {movie?.ageRating || movie?.rating}
                    </span>
                  )}
                </div>
                <p>
                  {cinemaData?.name || theater?.name || "My Cinemas"}
                  {showtime?.screenName && ` (${showtime.screenName})`}
                </p>
                <p>{cinemaData?.address || theater?.address || ""}</p>
                <p>
                  {showtime?.date || ""}
                  {showtime?.time ? ` • ${showtime.time}` : ""}
                </p>
              </div>
            </div>

            {/* Seats */}
            <div className="sc-seat-section">
              <div className="sc-seat-label">Seat Info</div>
              {selectedSeats?.length > 0 ? (
                selectedSeats.map((s, i) => (
                  <div key={s.id || i} className="sc-seat-row">
                    <span className="sc-seat-cat">
                      {s.ticketTypeDescription ||
                        s.areaName ||
                        s.category?.toUpperCase() ||
                        "REGULAR"}
                    </span>
                    <span className="sc-seat-num">
                      {s.displayNumber ||
                        `${s.row}${s.number}` ||
                        `Seat ${i + 1}`}
                    </span>
                  </div>
                ))
              ) : (
                <div className="sc-seat-row">
                  <span className="sc-seat-cat">REGULAR</span>
                  <span className="sc-seat-num">A1</span>
                </div>
              )}
            </div>

            {/* Prices */}
            <div className="sc-price-section">
              <div className="sc-price-row">
                <span>
                  Net Price ({selectedSeats?.length || 0} × Ticket(s))
                </span>
                <span>₹{ticketTotal().toFixed(2)}</span>
              </div>
              {cart.length === 0 ? (
                <div className="sc-no-snacks">🍿🥤 No snacks added</div>
              ) : (
                cart.map((item) => (
                  <div key={item.id} className="sc-price-row">
                    <span>
                      {item.name} ×{item.quantity}
                    </span>
                    ₹{getSnackPrice(item.price) * item.quantity}
                    {/* ₹{roundTo2((item.price * item.quantity) * (1 + fbGstPercent / 100))} */}
                  </div>
                ))
              )}
              {convFee() > 0 && (
                <>
                  <div className="sc-price-row sc-conv-header">
                    <span>Convenience Fees</span>
                    <span>₹{convFee().toFixed(2)}</span>
                  </div>
                  <div className="sc-conv-details">
                    <div className="sc-price-row">
                      <span>Base Amount</span>
                      <span>₹{(convFee() / 1.18).toFixed(2)}</span>
                    </div>
                    <div className="sc-price-row">
                      <span>IGST @ 18%</span>
                      <span>₹{(convFee() - convFee() / 1.18).toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="sc-grand-total">
              <span>Grand Total</span>
              <span>₹{grandTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
      {/* end sc-body */}
    </div>
  );
};

export default SnacksSelection;